// The Plant data, Signals and Excursions stage bodies under the Advisory strip
// (polish.P28a, #715). Locked comp: `.impeccable/mocks/decision/advisory-stages.html`,
// family A (an 8/4 row: the stage's own list as an Advisory table, and what that
// list needs beside it), with C's replay-window lanes in the Excursions 4.
// Every derivation lives in `EarlyStageBodies.transform.ts`.

import { useMemo, useState, type KeyboardEvent } from 'react';
import { SurfacePagePanel, Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import { AskAssistant } from '@/versions/v1/shared/AskAssistant';
import type { Alarm, Case } from '@/versions/v1/schema/case';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { TagMapping } from '@/versions/v1/schema/tag-mapping';
import type { FlowLandingRead } from './FlowLanding.transform';
import { severityChip } from './Scene.transform';
import {
  dcsAlarmMeta,
  dcsAlarmRows,
  excursionBodyRows,
  openedThisShift,
  plantDataBodyRows,
  plantDataMeta,
  replayLanes,
  signalBodyMeta,
  signalBodyRows,
  stampLabel,
  type DcsAlarmBodyRow,
  type ExcursionBodyRow,
  type ReplayLanes,
  type StageCaseLink,
} from './EarlyStageBodies.transform';
import { formatElapsedMinutes } from '@/versions/v1/shared/clock.derive';
import {
  STAGE_LABEL_CAPS,
  STAGE_ROW_SELECTED,
  STAGE_ROWS,
  STAGE_TABLE,
  STAGE_TABLE_SCROLL,
  STAGE_TD,
  STAGE_TD_FIRST,
  STAGE_TH,
  STAGE_TH_FIRST,
  STAGE_THEAD,
} from './stage-body-ui';

/** A list taller than the comp's 360 px body scrolls inside its card. */
const STAGE_LIST_SCROLL = 'max-h-[25rem] overflow-auto';

/** Instrument cell: the tag over the signal it carries. */
function InstrumentCell({ tag, name, note }: { tag: string; name: string; note?: string }) {
  return (
    <>
      <div className="font-mono text-xs font-semibold text-ink-primary">{tag}</div>
      <div className="mt-0.5 text-xs leading-snug text-muted">
        {name}
        {note ? <> · <span className="whitespace-nowrap text-[var(--state-warning)]">{note}</span></> : null}
      </div>
    </>
  );
}

/** A row's case links: one per open case the seed names, in queue order (#758). */
function CaseLinkButton({ links, onOpenCase }: { links: readonly StageCaseLink[]; onOpenCase: (caseId: string) => void }) {
  if (links.length === 0) return null;
  return (
    <span className="whitespace-nowrap">
      {links.map((link, index) => (
        <span key={link.caseId}>
          {index > 0 ? <span className="text-ink-tertiary" aria-hidden="true"> · </span> : null}
          <button
            type="button"
            data-stage-case-link={link.caseId}
            aria-label={`open case ${link.code}`}
            onClick={(event) => {
              event.stopPropagation();
              onOpenCase(link.caseId);
            }}
            className="whitespace-nowrap rounded font-mono text-xs text-ink-secondary transition-colors hover:text-ink-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]"
          >
            {link.code} ›
          </button>
        </span>
      ))}
    </span>
  );
}

/** Row selection: the wash marks the row the 4 beside it reads. */
function selectableRow(selected: boolean, onSelect: () => void) {
  return {
    'aria-selected': selected,
    tabIndex: 0,
    onClick: onSelect,
    onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect();
      }
    },
    className: cn(
      'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-500)]',
      selected && STAGE_ROW_SELECTED,
    ),
  };
}

// ---------------------------------------------------------------------------
// Plant data
// ---------------------------------------------------------------------------

export function PlantDataBody({
  rows,
  subprocessName,
}: {
  rows: readonly TagMapping[];
  subprocessName: (id: string) => string | undefined;
}) {
  const bodyRows = useMemo(() => plantDataBodyRows(rows, subprocessName), [rows, subprocessName]);
  return (
    <Commentable name="PlantData">
      <SurfacePagePanel title="I/O coverage" meta={plantDataMeta(bodyRows)} flush>
        <div className={STAGE_LIST_SCROLL}>
          <Table wrap={false} className={STAGE_TABLE}>
            <THead className={STAGE_THEAD}>
              <TR>
                <TH className={STAGE_TH_FIRST}>Instrument</TH>
                <TH className={STAGE_TH}>Circuit</TH>
                <TH className={cn(STAGE_TH, 'pr-4')}>Cadence</TH>
              </TR>
            </THead>
            <TBody data-stage-body-list="plant-data">
              {bodyRows.map((row) => (
                <TR key={row.key} data-plant-data-row={row.tag}>
                  <TD className={STAGE_TD_FIRST}><InstrumentCell tag={row.tag} name={row.name} /></TD>
                  <TD className={cn(STAGE_TD, 'text-xs text-ink-secondary')}>{row.circuit}</TD>
                  <TD className={cn(STAGE_TD, 'pr-4 text-xs text-ink-secondary')}>{row.cadence}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      </SurfacePagePanel>
    </Commentable>
  );
}

// ---------------------------------------------------------------------------
// Signals
// ---------------------------------------------------------------------------

const PRIORITY_PILL: Readonly<Record<Alarm['priority'], string>> = {
  P1: 'bg-[color-mix(in_srgb,var(--state-blocked)_9%,transparent)] text-[var(--state-blocked)]',
  P2: 'bg-[color-mix(in_srgb,var(--state-blocked)_9%,transparent)] text-[var(--state-blocked)]',
  P3: 'bg-[color-mix(in_srgb,var(--state-warning)_12%,transparent)] text-[var(--state-warning)]',
  P4: 'bg-[color-mix(in_srgb,var(--state-info)_12%,transparent)] text-[var(--state-info)]',
};

function DcsAlarmsPanel({
  rows,
  highlightVariableId,
  cursorLabel,
  canAck,
  onAck,
}: {
  rows: readonly DcsAlarmBodyRow[];
  highlightVariableId?: string;
  cursorLabel: (raisedAt: string) => string;
  canAck: boolean;
  onAck: (alarmId: string) => void;
}) {
  return (
    <Commentable name="DcsAlarms">
      <SurfacePagePanel title="Already alarmed by the DCS" meta={dcsAlarmMeta(rows)} flush className="h-full">
        <ul data-dcs-alarm-list className={STAGE_ROWS}>
          {rows.map((alarm) => (
            <li
              key={alarm.id}
              data-dcs-alarm={alarm.code}
              className={cn(
                'flex min-w-0 items-center justify-between gap-3 px-4 py-2',
                (alarm.priority === 'P1' || alarm.priority === 'P2') && 'shadow-[inset_3px_0_0_var(--state-blocked)]',
                highlightVariableId && alarm.variableId === highlightVariableId && STAGE_ROW_SELECTED,
              )}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-xs font-medium text-ink-primary">{alarm.code}</span>
                  <span className={cn('whitespace-nowrap rounded-full px-2 py-px text-[10px] font-semibold uppercase tracking-[0.05em]', PRIORITY_PILL[alarm.priority])}>
                    {alarm.priorityLabel}
                  </span>
                </div>
                <p className="mt-0.5 text-sm font-medium leading-snug text-ink-primary">{alarm.message}</p>
                <p className="mt-0.5 font-mono text-[10px] tabular-nums text-muted">
                  {alarm.tag ? `${alarm.tag} · ` : ''}{cursorLabel(alarm.raisedAt)}
                </p>
              </div>
              {canAck && alarm.state === 'active' ? (
                <button
                  type="button"
                  onClick={() => onAck(alarm.id)}
                  className="shrink-0 rounded-lg border border-[var(--hairline-strong)] px-2.5 py-0.5 text-xs font-semibold text-ink-secondary transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]"
                >
                  Ack
                </button>
              ) : (
                <span className="shrink-0 text-xs text-muted">{alarm.state}</span>
              )}
            </li>
          ))}
        </ul>
      </SurfacePagePanel>
    </Commentable>
  );
}

export function SignalsBody({
  read,
  cases,
  alarms,
  cursorLabel,
  canAck,
  onAck,
  onOpenCase,
}: {
  read: FlowLandingRead;
  cases: readonly Case[];
  /** DCS alarms as the plant raised them. */
  alarms: readonly Alarm[];
  cursorLabel: (raisedAt: string) => string;
  canAck: boolean;
  onAck: (alarmId: string) => void;
  onOpenCase: (caseId: string) => void;
}) {
  const rows = useMemo(() => signalBodyRows(read, cases, alarms), [read, cases, alarms]);
  const alarmRows = useMemo(
    () => dcsAlarmRows(alarms, (alarm) => severityChip(alarm).label),
    [alarms],
  );
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selected = rows.find((row) => row.key === selectedKey) ?? rows[0];
  return (
    <div className="grid grid-cols-12 gap-3">
      <div className="col-span-12 lg:col-span-8">
        <Commentable name="Signals">
          <SurfacePagePanel
            title="Active SPC signals"
            meta={signalBodyMeta(rows)}
            actions={<AskAssistant threadId="thread-adv-ph-alarm" label="Ask about ZN-PH-01" />}
            flush
            className="h-full"
          >
            <div className={STAGE_TABLE_SCROLL}>
              <Table wrap={false} className={STAGE_TABLE}>
                <THead className={STAGE_THEAD}>
                  <TR>
                    <TH className={STAGE_TH_FIRST}>Instrument</TH>
                    <TH className={STAGE_TH}>Rules fired</TH>
                    <TH className={STAGE_TH}>Cadence</TH>
                    <TH className={cn(STAGE_TH, 'text-right')}>Excursions</TH>
                    <TH className={cn(STAGE_TH, 'pr-4')}>Case</TH>
                  </TR>
                </THead>
                <TBody data-stage-body-list="signals">
                  {rows.map((row) => (
                    <TR
                      key={row.key}
                      data-signal-row={row.tag}
                      {...selectableRow(row === selected, () => setSelectedKey(row.key))}
                    >
                      <TD className={cn(STAGE_TD_FIRST, 'min-w-44')}>
                        <InstrumentCell tag={row.tag} name={row.name} note={row.dcsAlarmCode ? `DCS alarm ${row.dcsAlarmCode}` : undefined} />
                      </TD>
                      <TD className={cn(STAGE_TD, 'text-xs leading-snug text-ink-secondary')}>{row.rules.join(' · ')}</TD>
                      <TD className={cn(STAGE_TD, 'text-xs text-ink-secondary')}>{row.cadence}</TD>
                      <TD className={cn(STAGE_TD, 'text-right text-sm font-semibold tabular-nums text-ink-primary')}>{row.excursionCount}</TD>
                      <TD className={cn(STAGE_TD, 'pr-4')}><CaseLinkButton links={row.caseLinks} onOpenCase={onOpenCase} /></TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          </SurfacePagePanel>
        </Commentable>
      </div>
      <div className="col-span-12 lg:col-span-4">
        <DcsAlarmsPanel
          rows={alarmRows}
          highlightVariableId={selected?.dcsAlarmCode ? selected.variableId : undefined}
          cursorLabel={cursorLabel}
          canAck={canAck}
          onAck={onAck}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Excursions
// ---------------------------------------------------------------------------

function HeldCell({ row, rank }: { row: ExcursionBodyRow; rank: number }) {
  return (
    <div className="grid grid-cols-[2.5rem_minmax(0,1fr)] items-center gap-2">
      <span className="text-sm font-semibold tabular-nums text-ink-primary">{formatElapsedMinutes(row.heldMinutes)}</span>
      <span className="relative h-[5px] overflow-hidden rounded-full bg-[var(--hairline)]" aria-hidden="true">
        <span
          data-held-bar={rank === 0 ? 'top' : undefined}
          className={cn('absolute inset-y-0 left-0 rounded-full', rank === 0 ? 'bg-[var(--color-accent-500)]' : 'bg-[var(--ink-secondary)]')}
          style={{ width: `${row.heldPct}%` }}
        />
      </span>
    </div>
  );
}

function ReplayWindowLanes({ lanes, selectedTag }: { lanes: ReplayLanes; selectedTag?: string }) {
  const gridLines = (
    <>
      <span aria-hidden="true" className="absolute inset-y-0 border-l border-dashed border-[var(--hairline-strong)]" style={{ left: `${lanes.windowStartPct}%` }} />
      {lanes.shiftStartPct !== null ? (
        <span aria-hidden="true" className="absolute inset-y-0 border-l border-dashed border-[var(--hairline-strong)]" style={{ left: `${lanes.shiftStartPct}%` }} />
      ) : null}
      <span aria-hidden="true" className="absolute inset-y-0 right-0 border-l-[1.5px] border-ink-primary" />
    </>
  );
  return (
    <div className="px-4 pb-2 pt-2.5" data-replay-lanes>
      {lanes.lanes.map((lane) => (
        <div
          key={lane.tag}
          data-replay-lane={lane.tag}
          className={cn(
            'grid h-[30px] grid-cols-[5.5rem_minmax(0,1fr)_2.75rem] items-center gap-2.5 rounded-md',
            lane.tag === selectedTag && 'bg-[color-mix(in_srgb,var(--color-accent-500)_8%,transparent)]',
          )}
        >
          <span className="truncate pl-1 font-mono text-xs font-medium text-ink-primary">{lane.tag}</span>
          <span className="relative h-3 rounded-[3px] bg-[var(--hairline)]">
            {gridLines}
            {lane.bands.map((band) => (
              <span
                key={band.id}
                data-lane-band={band.id}
                className={cn(
                  'absolute inset-y-0 rounded-[3px] border border-[color-mix(in_srgb,var(--state-blocked)_45%,transparent)] bg-[color-mix(in_srgb,var(--state-blocked)_9%,transparent)] opacity-[0.85]',
                  !band.beforeWindow && 'border-l-2 border-l-[var(--state-blocked)]',
                )}
                style={{ left: `${band.leftPct}%`, right: 0 }}
              />
            ))}
            {lane.beforeWindow ? (
              <span aria-hidden="true" className="absolute left-[3px] top-1/2 -translate-y-1/2 text-[10px] leading-none text-muted">◂</span>
            ) : null}
            {lane.dcsMarks.map((mark) => (
              <span
                key={mark.code}
                data-lane-dcs={mark.code}
                title={`DCS alarm ${mark.code}`}
                className="absolute -top-1 h-5 border-l-2 border-[var(--state-warning)]"
                style={{ left: `${mark.leftPct}%` }}
              />
            ))}
          </span>
          <span className="pr-1 text-right text-xs font-semibold tabular-nums text-ink-primary">{formatElapsedMinutes(lane.heldMinutes)}</span>
        </div>
      ))}
      <div className="mt-1 grid grid-cols-[5.5rem_minmax(0,1fr)_2.75rem] gap-2.5 text-[10px] tabular-nums text-muted">
        <span />
        <span className="relative h-3.5">
          <span className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: `${lanes.windowStartPct}%` }}>{lanes.startLabel}</span>
          <span className="absolute right-0 whitespace-nowrap">{lanes.cursorLabel}</span>
        </span>
        <span />
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[10px] text-muted">
        <span>◂ before {stampLabel(lanes.windowStart)}</span>
        <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="h-2.5 border-l-2 border-[var(--state-warning)]" />DCS alarm</span>
        <span>right = longest held</span>
      </div>
    </div>
  );
}

export function ExcursionsBody({
  read,
  cases,
  alarms,
  tagMappings,
  windowStart,
  cursor,
  onOpenCase,
}: {
  read: FlowLandingRead;
  cases: readonly Case[];
  alarms: readonly Alarm[];
  tagMappings: readonly TagMapping[];
  windowStart: SeriesTime;
  cursor: SeriesTime;
  onOpenCase: (caseId: string) => void;
}) {
  const rows = useMemo(() => excursionBodyRows(read, tagMappings, cases), [read, tagMappings, cases]);
  const lanes = useMemo(() => replayLanes(rows, alarms, windowStart, cursor), [rows, alarms, windowStart, cursor]);
  const opened = useMemo(() => openedThisShift(rows, cursor), [rows, cursor]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = rows.find((row) => row.id === selectedId) ?? rows[0];
  return (
    <div className="grid grid-cols-12 gap-3">
      <div className="col-span-12 lg:col-span-8">
        <Commentable name="Excursions">
          <SurfacePagePanel title="Active excursions" meta="longest held first" flush className="h-full">
            <div className={STAGE_LIST_SCROLL}>
              <Table wrap={false} className={STAGE_TABLE}>
                <THead className={STAGE_THEAD}>
                  <TR>
                    <TH className={STAGE_TH_FIRST}>Instrument</TH>
                    <TH className={STAGE_TH}>Rule</TH>
                    <TH className={cn(STAGE_TH, 'w-44')}>Held</TH>
                    <TH className={cn(STAGE_TH, 'text-right')}>Since</TH>
                    <TH className={cn(STAGE_TH, 'pr-4')}>Case</TH>
                  </TR>
                </THead>
                <TBody data-stage-body-list="excursions">
                  {rows.map((row, index) => (
                    <TR
                      key={row.id}
                      data-excursion-row={row.id}
                      {...selectableRow(row === selected, () => setSelectedId(row.id))}
                    >
                      <TD className={cn(STAGE_TD_FIRST, 'min-w-40')}><InstrumentCell tag={row.tag} name={row.name} /></TD>
                      <TD className={cn(STAGE_TD, 'text-xs text-ink-secondary')}>{row.rule}</TD>
                      <TD className={cn(STAGE_TD, 'w-44')}><HeldCell row={row} rank={index} /></TD>
                      <TD className={cn(STAGE_TD, 'whitespace-nowrap text-right font-mono text-xs tabular-nums text-muted')}>{stampLabel(row.since)}</TD>
                      <TD className={cn(STAGE_TD, 'pr-4')}><CaseLinkButton links={row.caseLink ? [row.caseLink] : []} onOpenCase={onOpenCase} /></TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          </SurfacePagePanel>
        </Commentable>
      </div>
      <div className="col-span-12 lg:col-span-4">
        <Commentable name="ReplayWindow">
          <SurfacePagePanel title="Replay window" meta={lanes.rangeLabel} flush className="h-full">
            <ReplayWindowLanes lanes={lanes} selectedTag={selected?.tag} />
            <div className="border-t border-[var(--hairline)] px-4 pb-3 pt-2.5" data-opened-this-shift>
              <span className={STAGE_LABEL_CAPS}>Opened this shift</span>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary">{opened.count}</span>
                <span className="text-xs leading-snug text-muted">
                  <span className="font-semibold text-ink-secondary">{opened.since}</span>
                  {opened.detail ? ` · ${opened.detail}` : ''}
                </span>
              </div>
            </div>
          </SurfacePagePanel>
        </Commentable>
      </div>
    </div>
  );
}
